<template>
  <div class="fundTransfers">
    <sikanii-list-page
      title="Fund Transfers"
      :columns="columnsLoanPayment"
      :rows="fundTransfers"
      :table-loading="tableLoading"
      show-search
      :show-profile-photo="true"
    >
      <template slot="table-header">
        <agent-filter @filter="(id)=>getFundTransfers(id)"/>
      </template>
      <template slot="right-extra-header-actions">
        <div class="d-flex align-self-center align-items-start">
          <sikanii-button
            v-responsive="$store.getters['app/largeScreen']"
            name="manage_fund_transfer"
            icon="SettingsIcon"
            label="Transfer"
            tool-tip-text="Transfer funds from one account to another (CRUD)"
            @click="showFundTransferModal()"
          />
          <sikanii-dropdown
            v-responsive="$store.getters['app/smallScreen']"
            left
          >
            <sikanii-dropdown-item
              icon="SettingsIcon"
              text="Fund Transfer"
              @click="showFundTransferModal()"
            />
          </sikanii-dropdown>
        </div>
      </template>
      <template #sender_photo="{row}">
        <sikanii-profile-photo :photo="row.item.sender_photo" />
      </template>
      <template #recipient_photo="{row}">
        <sikanii-profile-photo :photo="row.item.recipient_photo" />
      </template>
      <template #amount="{row}">
        <b-badge
          :style="row.item.status==='reversed'?'text-decoration:line-through;':''"
          variant="primary"
        >
          {{ $store.getters['auth/currency'] }}{{ row.item.amount | currency }}
        </b-badge>
      </template>
      <template #status="{row}">
        <b-badge :variant="row.item.status==='transferred'?'light-primary':'light-danger'">
          {{ row.item.status.toString().toUpperCase() }}
        </b-badge>
      </template>
      <template #sender="{row}">
        {{ row.item.sender }}<b-badge variant="light-danger">
          {{ row.item.sender_account_type.toString().toUpperCase() }}
        </b-badge>
      </template>
      <template #recipient="{row}">
        {{ row.item.recipient }}<b-badge variant="light-primary">
          {{ row.item.recipient_account_type.toString().toUpperCase() }}
        </b-badge>
      </template>
      <template #action="{row}">
        <sikanii-button
          v-if="row.item.status==='transferred' && $ability.can('reverse-transactions', 'savings_and_loans')"
          icon="RefreshCwIcon"
          :icon-only="true"
          variant="flat-primary"
          tool-tip-text="Reverse transfer"
          @click="showReverseConfirmation(row.item.id,row.item.reference)"
        />
        <sikanii-button
          icon="EyeIcon"
          :icon-only="true"
          variant="flat-primary"
          tool-tip-text="View more details"
          @click="showTransactionDetails(row.item)"
        />
      </template>
    </sikanii-list-page>
    <fund-transfer-setup
      :modal-opened="fundTransferModalOpened"
      :edit-id="editFundTransferId"
      @modalClosed="closeModal('transfer')"
    />
    <sikanii-modal
      title="Confirm Reversal"
      ok-text="Yes, Proceed"
      :show="reverseConfirmationOpened"
      @onClose="()=>{reverseConfirmationOpened=false}"
      @onOk="reverseTransaction()"
    >
      <p>You are about to reverse this transaction(<b>{{ transactionRef }}</b>), please confirm to proceed.</p>
    </sikanii-modal>
    <sikanii-modal
      hide-footer
      :title="`Transaction Details - ${transactionRef}`"
      ok-text="Yes, Proceed"
      :show="transactionDetailsOpened"
      @onClose="()=>{transactionDetailsOpened=false}"
    >
      <div class="row">
        <div class="col-12">
          <b>NARRATION</b>
        </div>
        <div class="col-12">
          <p>{{ transactionNarration || 'N/A' }}</p>
        </div>
      </div>
      <div class="row mt-2">
        <div class="col-12">
          <b>DATE AND TIME</b>
        </div>
        <div class="col-12">
          <p>{{ transactionFullDate }}</p>
        </div>
        <div class="col-12 mt-1">
          <b-badge variant="primary">
            <FeatherIcon icon="CheckIcon" />
            Completed Successfully
          </b-badge>
        </div>
      </div>
    </sikanii-modal>
  </div>
</template>

<script>
import SikaniiButton from '@/layouts/components/SikaniiButton.vue'
import showToast from '@/libs/mixins/showToast'
import SikaniiListPage from '@/layouts/components/SikaniiListPage.vue'
import FundTransferSetup from '@/views/susu-savings/fund-transfers/FundTransferSetup.vue'
import confirm from '@/libs/mixins/confirm'
import SikaniiDropdown from '@/layouts/components/SikaniiDropdown.vue'
import SikaniiDropdownItem from '@/layouts/components/SikaniiDropdownItem.vue'
import { BBadge } from 'bootstrap-vue'
import SikaniiProfilePhoto from '@/layouts/components/SikaniiProfilePhoto'
import SikaniiModal from '@/layouts/components/SikaniiModal'
import AgentFilter from '@/layouts/components/AgentFilter'

export default {
  name: 'Index',
  components: {
    AgentFilter,
    SikaniiModal,
    SikaniiProfilePhoto,
    SikaniiDropdownItem,
    SikaniiDropdown,
    FundTransferSetup,
    SikaniiListPage,
    SikaniiButton,
    BBadge,
  },
  mixins: [showToast, confirm],
  data() {
    return {
      editFundTransferId: null,
      fundTransferModalOpened: false,
      tableLoading: false,
      transactionId: null,
      transactionRef: null,
      transactionDetailsOpened: false,
      transactionNarration: '',
      transactionFullDate: '',
      reverseConfirmationOpened: false,
    }
  },
  computed: {
    fundTransfers() {
      return this.$store.getters['susuSavings/fundTransfers']
    },
    columnsLoanPayment() {
      const show = this.$store.getters['app/isLargeScreen']
      return [
        {
          key: 'index',
          label: '#',
        },
        {
          key: 'reference',
          label: 'REFERENCE',
        },
        {
          key: 'sender_photo',
          label: 'Sender Photo',
        },
        {
          key: 'sender',
          label: 'Sender',
          sortable: show,
        },
        {
          key: 'recipient_photo',
          label: 'Recipient Photo',
        },
        {
          key: 'recipient',
          label: 'Recipient',
          sortable: show,
        },
        {
          key: 'agent',
          label: 'Agent',
          sortable: show,
        },
        {
          key: 'amount',
          label: 'Amount',
          sortable: show,
        },
        {
          key: 'status',
          label: 'Status',
        },
        {
          key: 'date',
          label: 'Date',
        },
        {
          key: 'action',
          label: 'Action',
        },
      ]
    },
  },
  mounted() {
    this.getFundTransfers()
  },
  methods: {
    reverseTransaction() {
      this.tableLoading = true
      this.$http.post(`susu-savings/fund-transfers/${this.transactionId}/reverse`)
        .then(res => {
          this.$store.commit('susuSavings/updateFundTransfer', { id: this.transactionId, status: 'reversed' })
        })
        .catch(err => {
          if (err.response.status === 403) {
            this.showInfo('You are not authorized to reverse this transaction')
          } else if (err.response.status === 402) {
            this.showInfo('Transactions are closed')
          } else {
            this.showError('Failed to reverse transaction')
          }
        })
        .finally(() => {
          this.tableLoading = false
          this.reverseConfirmationOpened = false
        })
    },
    showReverseConfirmation(id, reference) {
      this.transactionId = id
      this.transactionRef = reference
      this.reverseConfirmationOpened = true
    },
    showTransactionDetails(payment) {
      this.transactionNarration = payment.narration
      this.transactionRef = payment.reference
      this.transactionFullDate = payment.full_date
      this.paymentSource = payment.is_direct_transfer ? 'Funds Transfer' : 'Normal Transaction'
      this.transactionDetailsOpened = true
    },
    editLoanPayment(id) {
      this.editFundTransferId = id
      this.showFundTransferModal()
    },
    closeModal(type) {
      // eslint-disable-next-line default-case
      switch (type) {
        case 'transfer':
          this.fundTransferModalOpened = false
          this.editFundTransferId = null
          break
      }
    },
    showFundTransferModal() {
      this.fundTransferModalOpened = true
    },
    getFundTransfers(agentId = null) {
      this.tableLoading = true
      this.$http.get(`susu-savings/transfers?agent_id=${agentId || ''}`)
        .then(res => {
          this.$store.commit('susuSavings/setFundTransfers', res.data)
        }).finally(() => {
          this.tableLoading = false
        })
    },
  },
}
</script>
